import React, { useState } from "react";
import { GitCompare, Sparkles } from "lucide-react";
import DNALoader from "../components/DNALoader";
import ScoreGauge from "../components/ScoreGauge";
import { compareProducts, displayName } from "../lib/api";

const ratingColor = (r) => (r === "red" ? "#FF3B3B" : r === "yellow" ? "#FFB800" : "#00C853");

export default function Compare() {
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);

  const run = async (e) => {
    e.preventDefault();
    if (!p1.trim() || !p2.trim()) return;
    setLoading(true);
    setErr(null);
    setResult(null);
    try {
      const r = await compareProducts(p1.trim(), p2.trim());
      setResult(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="compare-page" className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--text-3)] mb-3">
          <GitCompare className="h-3.5 w-3.5" /> Head to head
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">
          Compare two <span className="text-[color:var(--lime)]">products</span>
        </h1>
        <p className="mt-3 text-[color:var(--text-2)] max-w-xl mx-auto">
          Side-by-side breakdown — scores, ingredients, and warnings.
        </p>
      </div>

      <form onSubmit={run} className="grid md:grid-cols-[1fr_auto_1fr] gap-3 items-center mb-10">
        <input
          data-testid="compare-input-1"
          className="surface rounded-xl px-4 py-3 outline-none focus:border-[color:var(--lime)]"
          value={p1}
          onChange={(e) => setP1(e.target.value)}
          placeholder="Product A — e.g. Nutella"
        />
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-center text-[color:var(--text-3)] px-2">vs</div>
        <input
          data-testid="compare-input-2"
          className="surface rounded-xl px-4 py-3 outline-none focus:border-[color:var(--lime)]"
          value={p2}
          onChange={(e) => setP2(e.target.value)}
          placeholder="Product B — e.g. Biscoff Spread"
        />
        <div className="md:col-span-3 flex justify-center">
          <button type="submit" className="lime-btn rounded-xl px-6 py-3 text-sm inline-flex items-center gap-2" data-testid="compare-submit">
            <Sparkles className="h-4 w-4" /> Compare now
          </button>
        </div>
      </form>

      {loading && <DNALoader label="Comparing analyses..." />}
      {err && (
        <div className="text-center text-[color:var(--danger)] font-mono text-sm" data-testid="compare-error">
          {err}
        </div>
      )}

      {result && (
        <div className="grid md:grid-cols-2 gap-5" data-testid="compare-results">
          <CompareCol side="A" data={result.product1} />
          <CompareCol side="B" data={result.product2} />
        </div>
      )}
    </div>
  );
}

const CompareCol = ({ side, data }) => {
  if (!data) return null;
  const color = ratingColor(data.overallRating);
  const nh = data.nutritionHighlights || {};
  return (
    <div className="surface rounded-xl p-5" data-testid={`compare-col-${side.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--text-3)]">Side {side}</div>
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color }}>
          {data.overallRating?.toUpperCase()}
        </div>
      </div>
      <div className="flex items-center gap-4 mb-4">
        <ScoreGauge score={data.overallScore} rating={data.overallRating} size={120} />
        <div className="min-w-0">
          <div className="font-display text-xl font-semibold truncate" title={data.productName}>{displayName(data.productName, 28)}</div>
          <div className="text-xs text-[color:var(--text-3)] truncate">{data.brand}</div>
          <p className="text-sm text-[color:var(--text-2)] mt-2 line-clamp-3">{data.overallVerdict}</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center mt-2">
        <MiniStat label="Red" value={nh.redIngredients || 0} color="#FF3B3B" />
        <MiniStat label="Amber" value={nh.yellowIngredients || 0} color="#FFB800" />
        <MiniStat label="Green" value={nh.greenIngredients || 0} color="#00C853" />
      </div>
      <div className="mt-4 space-y-2">
        {(data.ingredients || []).slice(0, 8).map((ing, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <span className={`h-2 w-2 rounded-full ${ing.rating === "red" ? "dot-red" : ing.rating === "yellow" ? "dot-yellow" : "dot-green"}`} />
            <span className="text-[color:var(--text-2)] truncate">{ing.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const MiniStat = ({ label, value, color }) => (
  <div className="surface-elev rounded-lg py-2">
    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[color:var(--text-3)]">{label}</div>
    <div className="font-display text-2xl font-bold" style={{ color }}>{value}</div>
  </div>
);
